import { create } from 'zustand';
import type {
  CheckoutStep,
  CheckoutStepStatus,
  CustomerInfoFormValues,
  InstallationFormValues,
  PhoneFormValues,
} from '@t/checkout.types';
import { CPQ_CHECKOUT_STATE_KEY } from '@t/session.types';
import {
  STEP_ORDER,
  STEP_FORM_FIELD,
  activeSteps,
  buildDefaultStepStatuses,
} from '@components/Checkout/utils/stepConfig';

export interface CheckoutState {
  stepStatuses: Record<CheckoutStep, CheckoutStepStatus>;
  activeStep: CheckoutStep;
  previousActiveStep: CheckoutStep | null;
  hasPhonePlan: boolean;
  hasInstallationRequired: boolean;
  termsAccepted: boolean;

  // Saved form data for each step
  customerInfo: CustomerInfoFormValues | null;
  installationInfo: InstallationFormValues | null;
  phoneInfo: PhoneFormValues | null;
}

interface CheckoutActions {
  setActiveStep: (step: CheckoutStep) => void;
  completeStep: (step: CheckoutStep) => void;
  editStep: (step: CheckoutStep) => void;
  enterInstallationEdit: () => void;
  setHasPhonePlan: (has: boolean) => void;
  setHasInstallationRequired: (has: boolean) => void;
  setTermsAccepted: (accepted: boolean) => void;
  setCustomerInfo: (info: CustomerInfoFormValues) => void;
  setInstallationInfo: (info: InstallationFormValues | null) => void;
  setPhoneInfo: (info: PhoneFormValues) => void;
  getNextStep: (current: CheckoutStep) => CheckoutStep | null;
  isAllCompleted: () => boolean;
  resetForBackToCart: () => void;
  reset: () => void;
}

// Fields persisted to localStorage (excludes sensitive form data and transient state)
interface PersistedCheckoutState {
  stepStatuses: Record<CheckoutStep, CheckoutStepStatus>;
  activeStep: CheckoutStep;
  hasPhonePlan: boolean;
  hasInstallationRequired: boolean;
  termsAccepted: boolean;
}

const loadFromLocalStorage = (): Partial<CheckoutState> => {
  const raw = localStorage.getItem(CPQ_CHECKOUT_STATE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          stepStatuses: parsed.stepStatuses,
          activeStep: parsed.activeStep,
          hasPhonePlan: parsed.hasPhonePlan,
          hasInstallationRequired: parsed.hasInstallationRequired ?? false,
          termsAccepted: parsed.termsAccepted,
        };
      }
    } catch {
      // ignore corrupt data
    }
  }
  return {};
};

const saveToLocalStorage = (state: CheckoutState): void => {
  const persisted: PersistedCheckoutState = {
    stepStatuses: state.stepStatuses,
    activeStep: state.activeStep,
    hasPhonePlan: state.hasPhonePlan,
    hasInstallationRequired: state.hasInstallationRequired,
    termsAccepted: state.termsAccepted,
  };
  try {
    localStorage.setItem(CPQ_CHECKOUT_STATE_KEY, JSON.stringify(persisted));
  } catch (error) {
    console.warn('Failed to persist checkout state to localStorage', error);
  }
};

const DEFAULT_STATE: CheckoutState = {
  stepStatuses: buildDefaultStepStatuses(),
  activeStep: 'customer',
  previousActiveStep: null,
  hasPhonePlan: false,
  hasInstallationRequired: false,
  termsAccepted: false,
  customerInfo: null,
  installationInfo: null,
  phoneInfo: null,
};

export const useCheckoutStore = create<CheckoutState & CheckoutActions>((set, get) => ({
  ...DEFAULT_STATE,
  ...loadFromLocalStorage(),

  setActiveStep: (step) => {
    set((state) => ({
      activeStep: step,
      stepStatuses: {
        ...state.stepStatuses,
        [step]: 'active',
      },
    }));
    saveToLocalStorage(get());
  },

  completeStep: (step) => {
    const state = get();
    // If editing a previously completed step, return to where we were
    // instead of advancing to the next sequential step
    const returnStep = state.previousActiveStep && state.previousActiveStep !== step ? state.previousActiveStep : null;
    const nextStep = returnStep ?? get().getNextStep(step);
    set({
      stepStatuses: {
        ...state.stepStatuses,
        [step]: 'completed',
        ...(nextStep ? { [nextStep]: state.stepStatuses[nextStep] === 'completed' ? 'completed' : 'active' } : {}),
      },
      activeStep: nextStep ?? state.activeStep,
      previousActiveStep: null,
    });
    saveToLocalStorage(get());
  },

  editStep: (step) => {
    const state = get();
    set({
      previousActiveStep: state.activeStep,
      activeStep: step,
      stepStatuses: {
        ...state.stepStatuses,
        [state.activeStep]:
          state.stepStatuses[state.activeStep] === 'active' ? 'completed' : state.stepStatuses[state.activeStep],
        [step]: 'active',
      },
    });
    saveToLocalStorage(get());
  },

  enterInstallationEdit: () => {
    const state = get();
    set({
      installationInfo: null,
      previousActiveStep: null,
      activeStep: 'installation',
      stepStatuses: {
        customer: state.stepStatuses.customer === 'completed' ? 'completed' : state.stepStatuses.customer,
        phone: state.stepStatuses.phone === 'completed' ? 'completed' : state.stepStatuses.phone,
        installation: 'active',
        review: 'pending',
      },
    });
    saveToLocalStorage(get());
  },

  // Not persisted: only called by useReconcileCheckoutSteps to sync the derived
  // cart flags, so persisting here can race a delayed cart load against
  // resetForBackToCart/reset and resurrect localStorage after it was cleared.
  setHasPhonePlan: (has) => {
    set({ hasPhonePlan: has });
  },

  setHasInstallationRequired: (has) => {
    set({ hasInstallationRequired: has });
  },

  setTermsAccepted: (accepted) => {
    set({ termsAccepted: accepted });
    saveToLocalStorage(get());
  },

  setCustomerInfo: (info) => set({ customerInfo: info }),

  setInstallationInfo: (info) => set({ installationInfo: info }),

  setPhoneInfo: (info) => set({ phoneInfo: info }),

  getNextStep: (current) => {
    const state = get();
    const steps = activeSteps(state);
    const idx = steps.indexOf(current);
    const next = idx >= 0 ? steps[idx + 1] : undefined;
    if (next) return next;
    if (state.previousActiveStep && state.previousActiveStep !== current) {
      return state.previousActiveStep;
    }
    return null;
  },

  isAllCompleted: () => {
    const state = get();
    return activeSteps(state).every((step) => state.stepStatuses[step] === 'completed');
  },

  resetForBackToCart: () => {
    const state = get();
    const { hasPhonePlan, hasInstallationRequired, customerInfo, installationInfo, phoneInfo } = state;
    const steps = activeSteps({ hasPhonePlan, hasInstallationRequired });

    const firstIncompleteIdx = steps.findIndex((step) => {
      const field = STEP_FORM_FIELD[step];
      return field ? state[field] == null : true;
    });
    const resumeIdx = firstIncompleteIdx === -1 ? steps.length - 1 : firstIncompleteIdx;
    const resumeStep = steps[resumeIdx];

    const stepStatuses = STEP_ORDER.reduce(
      (acc, step) => {
        const idx = steps.indexOf(step);
        if (idx === -1) {
          acc[step] = 'pending';
        } else if (idx < resumeIdx) {
          acc[step] = 'completed';
        } else if (idx === resumeIdx) {
          acc[step] = 'active';
        } else {
          acc[step] = 'pending';
        }
        return acc;
      },
      {} as Record<CheckoutStep, CheckoutStepStatus>
    );

    set({
      stepStatuses,
      activeStep: resumeStep,
      previousActiveStep: null,
      hasPhonePlan,
      hasInstallationRequired,
      termsAccepted: false,
      customerInfo,
      installationInfo,
      phoneInfo,
    });

    localStorage.removeItem(CPQ_CHECKOUT_STATE_KEY);
  },

  reset: () => {
    set(DEFAULT_STATE);
    localStorage.removeItem(CPQ_CHECKOUT_STATE_KEY);
  },
}));

export const hasCheckoutInProgress = (): boolean => {
  const raw = localStorage.getItem(CPQ_CHECKOUT_STATE_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.stepStatuses?.customer === 'completed';
  } catch {
    return false;
  }
};
